import Vue from 'vue'
import jwt from 'jsonwebtoken'
import Router from '../router'
import {
    HOME_PAGE_FINISHED_SLIDE_PAGE_COUNT,
    KEYCLOAK_CONFIG,
    MIN_TOKEN_VALIDITY,
    PRODUCTS
} from '../const'
import urls from './urls'
import Context from '../../Context'
import UIUtils from '../utils/UIUtils'

let KeycloakInstance

const ADMIN_EMPLOYEE_EMAIL = 'admin@fairtility.com'

const stringReplacer = (string, params = []) => {
    params.forEach(function (param) {
        string = string.replace('%s', param !== undefined ? param : '').replace(/\/$/g, '')
    })
    return string
}

const createQueryString = (params = {}) => {
    return Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== false)
        .map(key => `${key}=${params[key]}`)
        .join('&')
}

const handleException = error => {
    UIUtils.hideLoadingMask()
    UIUtils.showModal('error')
    throw error
}

const HTTP_STATUS = {
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    CONFLICT: 409,
    SERVICE_UNAVAILABLE: 503
}

const LAB_CONNECTOR_URL = {
    [PRODUCTS.EQ]: urls.EQ_LAB_CONNECTOR,
    [PRODUCTS.OQ]: urls.OQ_LAB_CONNECTOR
}

function handleAdminOperationError(error) {
    if ([HTTP_STATUS.CONFLICT, HTTP_STATUS.BAD_REQUEST].includes(error.status)) {
        throw new Error(error.body.message)
    }
    handleException(error)
}

export default {
    async getAuthHeader() {
        if (!this.hasValidToken()) {
            try {
                const refreshed = await KeycloakInstance.updateToken(MIN_TOKEN_VALIDITY)
                if (refreshed) console.warn('TOKEN REFRESHED')
            } catch (err) {
                console.warn('Failed to refresh token: session expired, user is logged out')
                this.authenticateUser(Router.currentRoute)
            }
        }

        return {
            headers: { Authorization: 'Bearer ' + KeycloakInstance.token }
        }
    },

    isUserEmployee() {
        try {
            const token = KeycloakInstance.token
            const userInfo = jwt.decode(token, {})
            return (
                userInfo &&
                userInfo.resource_access &&
                userInfo.resource_access.kidplus &&
                userInfo.resource_access.kidplus.roles.length &&
                userInfo.resource_access.kidplus.roles.contains('employee')
            )
        } catch (e) {
            return false
        }
    },
    isUserHaveKpiAccess() {
        try {
            const token = KeycloakInstance.token
            const userInfo = jwt.decode(token, {})
            return (
                userInfo &&
                userInfo.realm_access &&
                userInfo.realm_access.roles.length &&
                userInfo.realm_access.roles.contains('kpi-access')
            )
        } catch (e) {
            return false
        }
    },
    isAdminEmployee() {
        try {
            const token = KeycloakInstance.token
            const userInfo = jwt.decode(token, {})
            return userInfo && userInfo.email && userInfo.email === ADMIN_EMPLOYEE_EMAIL
        } catch (e) {
            return false
        }
    },
    init() {
        Context.router = Router

        const KeycloakJS = require('keycloak-js')
        const keycloakParams = KEYCLOAK_CONFIG[process.env.NODE_ENV]

        // Initialize KeyCloak
        KeycloakInstance = new KeycloakJS(keycloakParams)
    },

    /*** AUTHENTICATION VIA KEYCLOAK ***/
    hasValidToken() {
        return KeycloakInstance.token && !KeycloakInstance.isTokenExpired()
    },

    authenticateUser(route) {
        const redirectUri = window.location.origin + route.fullPath
        KeycloakInstance.init({
            onLoad: 'check-sso',
            redirectUri,
            silentCheckSsoRedirectUri: window.location.origin + '/static/html/silent-check-sso.html'
        }).then(async authenticated => {
            if (authenticated) {
                this.sendAuthAnalytics()

                await Vue.$store.dispatch('common/setUserData')

                await this.redirectToKidPlus(route)
            } else {
                console.log('init with login-required')
                KeycloakInstance.init({
                    onLoad: 'login-required',
                    redirectUri
                })
            }
        })
    },

    sendAuthAnalytics() {
        Vue.$ga.time({
            timingCategory: 'session',
            timingVar: 'user_authenticated',
            timingValue: new Date().getTime(),
            timingLabel: 'Session start time'
        })
        Vue.$ga.event({
            eventCategory: 'session',
            eventAction: 'user_authenticated',
            eventLabel: 'Session start event'
        })
    },

    async redirectToKidPlus(route) {
        if (!route.name) {
            await Vue.$store.dispatch('navigation/navigateHome')
        } else {
            const routeWithoutHash = { ...route, hash: '' }
            await Router.push(routeWithoutHash)
        }
    },

    signOut() {
        Vue.$ga.event({
            eventCategory: 'session',
            eventAction: 'logout',
            eventLabel: 'Logout event'
        })

        KeycloakInstance.logout()
    },

    /*** API ***/
    async fetchUserData() {
        const authHeader = await this.getAuthHeader()

        try {
            const { body: user } = await Vue.http.get(urls.USER_DATA, authHeader)
            return user
        } catch (e) {
            handleException(e)
        }
    },

    async fetchInstruments(tenantId) {
        const authHeader = await this.getAuthHeader()

        try {
            const { body } = await Vue.http.get(urls.INSTRUMENTS, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },

    async fetchPatients() {
        const authHeader = await this.getAuthHeader()

        try {
            const { body: patients } = await Vue.http.get(urls.PATIENTS, authHeader)
            return patients
        } catch (e) {
            handleException(e)
        }
    },

    async fetchPastSlides({
        start = null,
        end = null,
        freeSearchText = '',
        hoursSinceFertilization = false,
        page = 1,
        pageSize = HOME_PAGE_FINISHED_SLIDE_PAGE_COUNT
    }) {
        const authHeader = await this.getAuthHeader()
        const _params = {
            start,
            end,
            page,
            pageSize,
            _search: freeSearchText,
            hoursSinceFertilization
        }
        const url = `${urls.SLIDES}?${createQueryString(_params)}`

        try {
            const { body: patients } = await Vue.http.get(url, authHeader)
            return patients
        } catch (e) {
            handleException(e)
        }
    },

    async fetchPatient(id) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.PATIENT, [id])

        try {
            const { body: patient } = await Vue.http.get(url, authHeader)
            return patient
        } catch (e) {
            handleException(e)
        }
    },

    async fetchTreatment(id) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.TREATMENT, [id])

        try {
            const { body: treatment } = await Vue.http.get(url, authHeader)
            return treatment
        } catch (e) {
            handleException(e)
        }
    },

    async updateTreatment(id, delta) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.TREATMENT, [id])

        try {
            const { body: treatment } = await Vue.http.patch(url, delta, authHeader)
            return treatment
        } catch (e) {
            handleException(e)
        }
    },

    async fetchWell(id) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.WELL, [id]) + '?lazy=true'

        try {
            const { body: well } = await Vue.http.get(url, authHeader)
            return well
        } catch (e) {
            handleException(e)
        }
    },

    async updateWellsFertMethod(payload) {
        const authHeader = await this.getAuthHeader()
        try {
            const { body: well } = await Vue.http.patch(urls.UPDATE_WELLS, payload, authHeader)
            return well
        } catch (e) {
            handleException(e)
        }
    },

    async fetchNotes(holderId) {
        const authHeader = await this.getAuthHeader()

        const url = stringReplacer(urls.NOTES, [holderId])

        try {
            const { body: notes } = await Vue.http.get(url, authHeader)
            return notes
        } catch (e) {
            handleException(e)
        }
    },

    async addNote(note) {
        const authHeader = await this.getAuthHeader()

        try {
            await Vue.http.post(urls.ADD_NOTE, note, authHeader)
        } catch (e) {
            handleException(e)
        }
    },

    async deleteNote(note) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.NOTE, [note._id])

        try {
            await Vue.http.delete(url, authHeader)
        } catch (e) {
            handleException(e)
        }
    },

    async generateAgentDownloadUrl(tenantId, password) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.AGENTS, [tenantId])

        try {
            const { body } = await Vue.http.post(url, { password }, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },

    async updateWell(id, delta) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.WELL, [id])

        try {
            const { body: well } = await Vue.http.patch(url, delta, authHeader)
            return well
        } catch (e) {
            handleException(e)
        }
    },
    async updateInference(id, inferenceDelta) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.WELL, [id]) + '/inference-overriding'

        try {
            const { body: well } = await Vue.http.patch(url, inferenceDelta, authHeader)
            return well
        } catch (e) {
            handleException(e)
        }
    },
    async updateInstrument(id, instrument) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.UPDATE_INSTRUMENT, [id])

        try {
            const { body } = await Vue.http.patch(url, instrument, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },

    async share(recipient, message, link) {
        const authHeader = await this.getAuthHeader()
        try {
            await Vue.http.post(
                urls.USER_SHARE,
                {
                    recipient,
                    message,
                    links: link
                },
                authHeader
            )
        } catch (e) {
            handleException(e)
        }
    },

    async updatePatient(id, delta) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.PATIENT, [id])

        try {
            const { body: patient } = await Vue.http.patch(url, delta, authHeader)
            return patient
        } catch (e) {
            handleException(e)
        }
    },
    async updateUserProfile(delta) {
        const authHeader = await this.getAuthHeader()
        const url = urls.USER_PROFILE

        const { body: user } = await Vue.http.patch(url, delta, authHeader)
        return user
    },
    async updateTenantSettings(data, tenantId) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.UPDATE_TENANTS, [tenantId])

        const { body } = await Vue.http.patch(url, data, authHeader)
        return body
    },
    async updateClinicLogo(data, tenantId) {
        const authHeader = await this.getAuthHeader()
        authHeader.contentType = false
        authHeader.processData = false

        const url = stringReplacer(urls.UPLOAD_CLINIC_LOGO, [tenantId])

        const { body } = await Vue.http.post(url, data, authHeader)
        return body
    },
    async fetchPatientSharedTenant(cipher) {
        const url = stringReplacer(urls.PATIENT_SHARED_TENANT, [cipher])

        const { body } = await Vue.http.get(url)
        return body
    },

    async updateAvatar(delta) {
        const authHeader = await this.getAuthHeader()
        authHeader.contentType = false
        authHeader.processData = false

        const url = urls.USER_AVATAR

        const { body: user } = await Vue.http.patch(url, delta, authHeader)
        return user
    },

    async createShareToPatientUrl({ patientId, ...rest }) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.SHARE_TO_PATIENT, [patientId])

        try {
            const { body } = await Vue.http.post(url, { ...rest }, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },

    async getSharedToPatientTreatment({ cipher, birthYear }, token) {
        const url = `${urls.PATIENT_SHARED_TREATMENT}?cipher=${cipher}&birthYear=${birthYear}`
        const { body } = await Vue.http.get(url, {
            headers: { Authorization: 'Bearer ' + token }
        })
        return body
    },

    async createTenant(createTenantDto) {
        const authHeader = await this.getAuthHeader()
        const url = `${urls.TENANTS}`

        try {
            const { body } = await Vue.http.post(url, createTenantDto, authHeader)
            return body
        } catch (e) {
            handleAdminOperationError(e)
        }
    },
    async updateTenant(data, tenantId) {
        const authHeader = await this.getAuthHeader()
        const url = stringReplacer(urls.UPDATE_TENANT_ADMIN, [tenantId])

        try {
            const { body } = await Vue.http.patch(url, data, authHeader)
            return body
        } catch (e) {
            handleAdminOperationError(e)
        }
    },
    async getAllTenants() {
        const authHeader = await this.getAuthHeader()
        const url = `${urls.TENANTS}`

        try {
            const { body } = await Vue.http.get(url, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },
    async createUser(createUserDto) {
        const authHeader = await this.getAuthHeader()
        const url = `${urls.USERS}`

        try {
            const { body } = await Vue.http.post(url, createUserDto, authHeader)
            return body
        } catch (e) {
            handleAdminOperationError(e)
        }
    },
    async testConnection(testConnectionDto, product = PRODUCTS.EQ) {
        const authHeader = await this.getAuthHeader()
        const url = `${LAB_CONNECTOR_URL[product]}/test-connection`

        try {
            const { body } = await Vue.http.post(url, testConnectionDto, authHeader)
            return body
        } catch (e) {
            if (
                e.status === HTTP_STATUS.SERVICE_UNAVAILABLE ||
                e.status === HTTP_STATUS.UNAUTHORIZED
            )
                throw new Error(e.body.message)

            handleException(e)
        }
    },
    async createConnector(createConnectorDto, product = PRODUCTS.EQ) {
        const authHeader = await this.getAuthHeader()
        const url = `${LAB_CONNECTOR_URL[product]}`

        try {
            const { body } = await Vue.http.post(url, createConnectorDto, authHeader)
            return body
        } catch (e) {
            handleAdminOperationError(e)
        }
    },
    async updateConnector(id, updateConnectorDto, product = PRODUCTS.EQ) {
        const authHeader = await this.getAuthHeader()
        const url = `${LAB_CONNECTOR_URL[product]}/${id}`

        try {
            const { body } = await Vue.http.patch(url, updateConnectorDto, authHeader)
            return body
        } catch (e) {
            handleAdminOperationError(e)
        }
    },
    async getAllConnectors(product = PRODUCTS.EQ) {
        const authHeader = await this.getAuthHeader()
        const url = `${LAB_CONNECTOR_URL[product]}`

        try {
            const { body } = await Vue.http.get(url, authHeader)
            return body
        } catch (e) {
            handleException(e)
        }
    },
    async updateVRepro(syncDto) {
        const authHeader = await this.getAuthHeader()
        const { body } = await Vue.http.post(urls.UPDATE_VREPRO, syncDto, authHeader)
        return body
    },
    async updateEmr(syncDto) {
        const authHeader = await this.getAuthHeader()
        const { body } = await Vue.http.post(urls.UPDATE_EMR, syncDto, authHeader)
        return body
    },
    async getTreatmentLink({ treatmentExternalId, patientExternalId }) {
        const authHeader = await this.getAuthHeader()
        const url = `${urls.TREATMENT_LINK}?treatmentName=${treatmentExternalId}&patientIdentifier1=${patientExternalId}`
        const { body } = await Vue.http.get(url, authHeader)
        return body
    }
}
